import { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import dayjs from 'dayjs';
import API from '@/api/api';
import { useVendorCodesData } from '@/hooks/useVendorCodesData';
import { Row, Col, Modal, Card, Table, message, Tag, Space } from 'antd';
import { Typography, Form, Input, Select, Radio, DatePicker, Button, Upload } from 'antd';
import { UploadOutlined, SearchOutlined } from '@ant-design/icons';
import ManagingAdminLayout from '@layout/ManagingAdmin';
import moment from 'moment';
import clsx from 'clsx';

export default function MngVendorCodes() {
  const { vendorCodes, projects, loading, refetch } = useVendorCodesData();

  const [filteredVendorCodes, setFilteredVendorCodes] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [IsCreateWithNewCompany, setIsCreateWithNewCompany] = useState(false);
  const [EditVendorCode, setEditVendorCode] = useState(null);
  const [isShowVendorCodeFormModal, openShowVendorCodeFormModal] = useState(false);
  const [isShowBulkVendorCodeFormModal, openShowBulkVendorCodeFormModal] = useState(false);
  const [BulkVendorCodeProject, setBulkVendorCodeProject] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [isShowUploadResultModal, setIsShowUploadResultModal] = useState(false);
  const [VendorCodeForm] = Form.useForm();
  const [VendorCodeFormDisabled, setVendorCodeFormDisabled] = useState(false);

  useEffect(() => {
    handleSearch(searchText, vendorCodes);
  }, [vendorCodes, searchText]);

  useEffect(() => {
    if (isShowVendorCodeFormModal && EditVendorCode) {
      const _Vendor = { ...EditVendorCode };
      if (_Vendor.used && _Vendor.used_at) _Vendor.used_at = moment(_Vendor.used_at);
      if (_Vendor.is_notification_sent && _Vendor.notification_sent_at)
        _Vendor.end_date = moment(_Vendor.notification_sent_at);

      VendorCodeForm.setFieldsValue(_Vendor);

      if (
        (_Vendor?.project.status_code && _Vendor.project.status_code !== 'NOT_STARTED') ||
        _Vendor?.project.is_notification_sent
      ) {
        setVendorCodeFormDisabled(true);
      } else {
        setVendorCodeFormDisabled(false);
      }
    }
  }, [isShowVendorCodeFormModal, EditVendorCode, VendorCodeForm, setVendorCodeFormDisabled]);

  const handleSearch = (value, dataSource = vendorCodes) => {
    setSearchText(value);
    if (!value.trim()) {
      setFilteredVendorCodes(dataSource);
      return;
    }
    const filtered = dataSource.filter((vendorCode) => {
      const projectCode = vendorCode.project?.project_code?.toLowerCase() || '';
      const companyName = vendorCode.company?.company_name?.toLowerCase() || '';
      const vendorCodeValue = vendorCode.vendor_code?.toLowerCase() || '';
      const searchValue = value.toLowerCase();
      return (
        projectCode.includes(searchValue) ||
        companyName.includes(searchValue) ||
        vendorCodeValue.includes(searchValue)
      );
    });
    setFilteredVendorCodes(filtered);
  };

  const handleSearchReset = () => {
    setSearchText('');
    setFilteredVendorCodes(vendorCodes);
  };

  const VendorCodeCreate = () => (
    <Button
      onClick={() => {
        setEditVendorCode(null);
        openShowVendorCodeFormModal(true);
      }}
    >
      벤더코드 추가
    </Button>
  );

  const VendorCodeEdit = (vendor_code_raw) => (
    <Button
      onClick={() => {
        setEditVendorCode(vendor_code_raw);
        openShowVendorCodeFormModal(true);
      }}
      disabled={vendor_code_raw?.project && vendor_code_raw?.project.status_code != 'NOT_STARTED'}
    >
      수정하기
    </Button>
  );

  const deleteVendorCode = async (vendor_code) => {
    try {
      const is_confirmed_delete_vc = confirm(`${vendor_code.vendor_code}를 삭제하시겠습니까?`);
      if (is_confirmed_delete_vc) {
        await API.delete(`/admin/vendor_codes/${vendor_code.id}`);
        refetch();
      }
    } catch (error) {
      console.log(` ~ error => `, error);
    }
  };

  const VendorCodeDelete = (vendor_code_raw) => (
    <Button
      onClick={() => deleteVendorCode(vendor_code_raw)}
      disabled={vendor_code_raw?.project && vendor_code_raw?.project.status_code != 'NOT_STARTED'}
      danger
    >
      삭제하기
    </Button>
  );

  const handleSubmitVendorCode = async (values) => {
    try {
      if (EditVendorCode?.id) {
        await API.put(`/admin/vendor_codes/${EditVendorCode?.id}`, {
          id: EditVendorCode.id,
          ...values,
        });
        message.success('벤더코드가 성공적으로 수정되었습니다.');
      } else {
        await API.post('/admin/vendor_codes', values);
        message.success('벤더코드가 성공적으로 생성되었습니다.');
      }
      openShowVendorCodeFormModal(false);
      VendorCodeForm.resetFields();
      refetch();
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail || error.message || '벤더코드 저장에 실패했습니다.';
      message.error(errorMessage);
    }
  };

  const onChangeIsCreateVendorCodeWitNewCompany = (e) => {
    setIsCreateWithNewCompany(e.target.value);
  };

  const SearchCompanyInput = (props) => {
    const searchCompanies = async (value, cb) => {
      if (value) {
        const res_searhced_companies = await API.get('/admin/companies', {
          params: { company_code: value, company_name: value },
        });
        if (res_searhced_companies.data.length) {
          const searhced_companies = res_searhced_companies.data.map(({ id, company_name }) => ({
            label: company_name,
            value: id,
          }));
          cb(searhced_companies);
        }
      }
    };
    const debouncedSearchCompanies = useMemo(() => debounce(searchCompanies, 400), []);
    const [SearchedCompanies, setSearchedCompanies] = useState([]);
    const handleSearchCompanies = async (newValue) => {
      if (newValue) {
        await debouncedSearchCompanies(newValue, setSearchedCompanies);
      } else {
        setSearchedCompanies([]);
      }
    };
    const handleChangeVendorCompany = (newValue) => {
      props.onChange(newValue);
    };
    return (
      <Select
        className="w-100"
        showSearch
        value={props.value}
        placeholder={props.placeholder}
        style={props.style}
        disabled={props.disabled}
        defaultActiveFirstOption={false}
        showArrow={false}
        filterOption={false}
        onSearch={handleSearchCompanies}
        onChange={handleChangeVendorCompany}
        notFoundContent={null}
        options={SearchedCompanies}
      />
    );
  };

  const VendorCodesColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', className: 'text-center' },
    {
      title: 'Project Code',
      key: 'project_code',
      className: 'text-center',
      width: '17%',
      render: (_, record) =>
        (
          <>
            <Tag color="blue">{record.project?.id}</Tag> {record.project?.project_code}
          </>
        ) ?? '-',
    },
    {
      title: '기업명',
      key: 'company_name',
      className: 'text-center',
      width: '20%',
      render: (_, record) =>
        (
          <>
            <Tag color="geekblue">{record.company?.id}</Tag> {record.company?.company_name}
          </>
        ) ?? '-',
    },
    { title: '벤더코드', dataIndex: 'vendor_code', key: 'vendor_code', className: 'text-center', width: '13%' },
    {
      title: '사용유무',
      dataIndex: 'used',
      key: 'used',
      className: 'text-center',
      width: '12%',
      render: (_, record) =>
        record.project.status_code === 'NOT_STARTED' ? (
          <Tag className="bg-secondary text-white rounded fw-normal">프로젝트 미시작</Tag>
        ) : record.used ? (
          <Tag className="border-blue-9 bg-blue-2 text-blue-7 rounded-pill px-3">사용</Tag>
        ) : (
          <Tag color="red" className="rounded-pill">미사용</Tag>
        ),
    },
    {
      title: '사용일시',
      dataIndex: 'used_at',
      key: 'used_at',
      className: 'text-center',
      render: (_, record) => (record.used_at ? dayjs(record.used_at).format('YYYY-MM-DD') : 'ㅡ'),
    },
    {
      title: 'Action',
      key: 'action',
      className: 'text-center',
      width: '10%',
      render: (_, record) => (
        <>
          {VendorCodeEdit(record)}
          {VendorCodeDelete(record)}
        </>
      ),
    },
  ];

  const downloadVendorCodeTemplate = async () => {
    const response = await API.get(`/admin/vendor_codes/template/download`, {
      params: { project_id: BulkVendorCodeProject },
      responseType: 'blob',
    });
    const contentDisposition = response.headers['content-disposition'];
    const _project = projects.find(({ id }) => id === BulkVendorCodeProject);
    let filename = `벤더코드_벌크_생성_템플릿_${BulkVendorCodeProject}_${_project.project_code}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
      if (filenameMatch?.[1]) {
        filename = decodeURIComponent(filenameMatch[1]);
      } else {
        const fallbackMatch = contentDisposition.match(/filename="(.+)"/);
        if (fallbackMatch?.[1]) {
          filename = fallbackMatch[1];
        }
      }
    }
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const VendorCodeBulkCreate = () => (
    <Button onClick={() => openShowBulkVendorCodeFormModal(true)}>벤더코드 벌크 생성</Button>
  );

  const uploadVendorCodeTemplate = async (options) => {
    const { onSuccess, onError, file, onProgress } = options;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await API.post('/admin/vendor_codes/template/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (event) => {
          onProgress({ percent: (event.loaded / event.total) * 100 });
        },
      });
      setUploadResult({ fileName: file.name, success_count: res.data.success_count, fail_count: res.data.fail_count, fail_details: res.data.fail_details || [] });
      setIsShowUploadResultModal(true);
      if (res.data.success_count > 0) {
        message.success(`${file.name} 업로드 및 벤더코드 벌크 생성 완료!`);
      } else {
        message.error(`${file.name} 업로드 및 벤더코드 벌크 생성 실패!`);
      }
      onSuccess(res.data);
    } catch (err) {
      const failMessage = `${file.name} 업로드 및 벤더코드 벌크 생성 실패!`;
      const detailMessage = err.response?.data?.detail || err.message || '알 수 없는 오류';
      setUploadResult({ fileName: file.name, success_count: 0, fail_count: 1, fail_details: [{ row: 1, reason: detailMessage }], isError: true });
      setIsShowUploadResultModal(true);
      message.error(failMessage);
      onError(err);
    } finally {
      refetch();
      openShowBulkVendorCodeFormModal(false);
    }
  };

  return (
    <ManagingAdminLayout>
      <Typography.Title level={3}>벤더코드 관리</Typography.Title>
      <Modal
        title={'벤더코드 벌크 생성'}
        open={isShowBulkVendorCodeFormModal}
        onCancel={() => openShowBulkVendorCodeFormModal(false)}
        width="50%"
        centered
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <div className="text-center">
              <div className="mb-3">1. 벤더코드 생성 프로젝트 선택</div>
              <Select
                placeholder="프로젝트 선택"
                fieldNames={{ label: 'project_code', value: 'id' }}
                options={projects}
                onChange={(value) => setBulkVendorCodeProject(value)}
                className="w-100"
              />
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="text-center">
              <div className="mb-3">2. 벤더코드 생성 템플릿 다운로드</div>
              <Button onClick={downloadVendorCodeTemplate} disabled={!BulkVendorCodeProject}>
                벌크 생성 템플릿 다운로드
              </Button>
            </div>
          </Col>
          <Col xs={24} lg={8}>
            <div className="text-center">
              <div className="mb-3">3. 벤더코드 리스트 템플릿 업로드</div>
              <Upload customRequest={uploadVendorCodeTemplate} showUploadList={false}>
                <Button icon={<UploadOutlined />}>벤더코드 엑셀 업로드</Button>
              </Upload>
            </div>
          </Col>
        </Row>
      </Modal>

      <Modal
        title={uploadResult?.isError ? '업로드 실패' : '업로드 결과'}
        open={isShowUploadResultModal}
        onCancel={() => setIsShowUploadResultModal(false)}
        footer={[
          <Button key="close" onClick={() => setIsShowUploadResultModal(false)}>
            닫기
          </Button>,
        ]}
        width="60%"
      >
        {uploadResult && (
          <div>
            <Typography.Title level={4}>파일: {uploadResult.fileName}</Typography.Title>
            {uploadResult.isError ? (
              <div className="mb-3">
                <Tag color="red">오류 발생</Tag>
                <div className="mt-3">
                  <Typography.Title level={5}>오류 내용:</Typography.Title>
                  <div className="p-3 border rounded bg-light">{uploadResult.fail_details[0]?.reason}</div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <Tag color="green">성공: {uploadResult.success_count}개</Tag>
                  <Tag color="red">실패: {uploadResult.fail_count}개</Tag>
                </div>
                {uploadResult.fail_details.length > 0 && (
                  <div>
                    <Typography.Title level={5}>실패 상세 내역:</Typography.Title>
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {uploadResult.fail_details.map((detail, index) => (
                        <div key={index} className="mb-2 p-2 border rounded bg-light">
                          <strong>행 {detail.row}:</strong> {detail.reason}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      <Modal
        open={isShowVendorCodeFormModal}
        onCancel={() => {
          openShowVendorCodeFormModal(false);
          VendorCodeForm.resetFields();
        }}
        onOk={() => VendorCodeForm.validateFields().then(handleSubmitVendorCode)}
        title={EditVendorCode?.id ? '벤더코드 수정' : '벤더코드 생성'}
        okText={EditVendorCode?.id ? '수정 저장' : '생성하기'}
        destroyOnClose
        width="60%"
        bodyStyle={{ backgroundColor: '#f8f8f8' }}
      >
        <div className="d-flex align-items-center justify-content-end mb-4">
          <Typography.Title className="mb-0 me-2" level={5}>벤더코드 - 기업 연동방법:{' '}</Typography.Title>
          <Radio.Group onChange={onChangeIsCreateVendorCodeWitNewCompany} value={IsCreateWithNewCompany}>
            <Radio value={false}>기존 기업 연결</Radio>
            <Radio value={true}>신규 기업 할당</Radio>
          </Radio.Group>
        </div>

        <Form form={VendorCodeForm} layout="vertical" initialValues={{ vendor_code: null, project_id: null, project_code: null, company_id: null, company_name: null, company_code: null, notification_recipient_email: null, notification_recipient_name: null }}>
          <Row gutter={[12, 12]}><Col xs={24}><Typography.Title className="" level={5}>벤더코드 기본 설정</Typography.Title></Col></Row>
          <div className="border bg-white p-3 mb-3">
            {EditVendorCode?.project && (
              <Row gutter={[12, 12]}>
                <Col xs={24} lg={8}><Form.Item label="프로젝트명"><Input disabled value={EditVendorCode?.project.project_name_ko} /></Form.Item></Col>
                <Col xs={24} lg={8}><Form.Item label="프로젝트 Period"><Input disabled value={EditVendorCode?.project.period} /></Form.Item></Col>
                <Col xs={24} lg={8}><Form.Item label="프로젝트 상태"><Input disabled value={EditVendorCode?.project.status_code} /></Form.Item></Col>
              </Row>
            )}
            <Row gutter={[12, 12]}>
              <Col xs={24} lg={IsCreateWithNewCompany ? 12 : 8}><Form.Item name="vendor_code" label="벤더 코드"><Input placeholder="원청사의 벤더코드 입력 또는 자동 생성" disabled={VendorCodeFormDisabled} /></Form.Item></Col>
              <Col xs={24} lg={IsCreateWithNewCompany ? 12 : 8}><Form.Item name="project_id" label="프로젝트 선택"><Select fieldNames={{ label: 'project_code', value: 'id' }} options={projects} disabled={VendorCodeFormDisabled} /></Form.Item></Col>
              {!IsCreateWithNewCompany && (
                <Col xs={24} lg={8}>
                  <Form.Item name="company_id" label="기업명 검색 및 선택" className={clsx(EditVendorCode?.company && EditVendorCode?.company.company_name && 'mb-1')}>
                    <SearchCompanyInput placeholder="기업 검색 및 선택" style={{ width: 200 }} disabled={IsCreateWithNewCompany || VendorCodeFormDisabled} />
                  </Form.Item>
                  <span className="text-secondary">({EditVendorCode?.company && EditVendorCode?.company.company_name})</span>
                </Col>
              )}
            </Row>
          </div>

          {IsCreateWithNewCompany && (
            <>
              <Row gutter={[12, 12]}><Col xs={24}><Typography.Title className="" level={5}>신규 기업 추가</Typography.Title></Col></Row>
              <div className="border bg-white p-3 mb-3">
                <Row gutter={[12, 12]}>
                  <Col xs={24} lg={12}><Form.Item name="company_name" label="기업명"><Input disabled={!IsCreateWithNewCompany || VendorCodeFormDisabled} /></Form.Item></Col>
                  <Col xs={24} lg={12}><Form.Item name="company_code" label="사업자번호"><Input disabled={!IsCreateWithNewCompany || VendorCodeFormDisabled} /></Form.Item></Col>
                </Row>
              </div>
            </>
          )}

          <Row gutter={[12, 12]}><Col xs={24}><Typography.Title className="" level={5}>신청 안내메일 수신 설정</Typography.Title></Col></Row>
          <div className="border bg-white p-3 mb-3">
            <Row gutter={[12, 12]}>
              {typeof EditVendorCode?.used !== 'undefined' && (
                <>
                  <Col xs={24} lg={12}><Form.Item label="전송유무"><Input disabled value={EditVendorCode.is_notification_sent ? '전송' : '미전송'} /></Form.Item></Col>
                  <Col xs={24} lg={12}><Form.Item label="신청 안내메일 발송일"><Input disabled value={EditVendorCode.used ? EditVendorCode.notification_sent_at : '미사용'} /></Form.Item></Col>
                </>
              )}
              <Col xs={24} lg={12}><Form.Item name="notification_recipient_email" label="수신 이메일"><Input disabled={VendorCodeFormDisabled} /></Form.Item></Col>
              <Col xs={24} lg={12}><Form.Item name="notification_recipient_name" label="수신 이름"><Input disabled={VendorCodeFormDisabled} /></Form.Item></Col>
            </Row>
          </div>
        </Form>
      </Modal>
      <Card title="벤더코드 리스트" extra={<><VendorCodeBulkCreate /> <VendorCodeCreate /></>} bodyStyle={{ padding: 10 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Input.Search
                placeholder="프로젝트 코드, 기업명, 벤더코드로 검색"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={() => handleSearch(searchText)}
                prefix={<SearchOutlined />}
                allowClear
                onClear={handleSearchReset}
                size="large"
              />
            </Col>
            <Col span={16}>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: '#666' }}>총 {filteredVendorCodes.length}개의 벤더코드</span>
              </div>
            </Col>
          </Row>

          <Table
            rowKey={(record) => record.id}
            columns={VendorCodesColumns}
            dataSource={filteredVendorCodes}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} 벤더코드`,
            }}
            locale={{
              emptyText: searchText
                ? `"${searchText}"에 대한 검색 결과가 없습니다.`
                : '등록된 벤더코드가 없습니다.',
            }}
          />
        </Space>
      </Card>
    </ManagingAdminLayout>
  );
}